<script lang="ts">
    import { onMount } from "svelte";
    import Btn from "../../components/Btn.svelte";
    import Container from "../../components/Container.svelte";
    import Headline from "../../components/Headline.svelte";
    import Hero from "../../components/Hero.svelte";
    import LightTheme from "../../components/LightTheme.svelte";
    import Subline from "../../components/Subline.svelte";
    import Cookies from "js-cookie";
    import { Loader, LoaderOptions } from "google-maps";
    import { each } from "svelte/internal";
    import MapIcon from "../../icons/MapIcon.svelte";
    import IconImg from "../../components/IconImg.svelte";
    import Divider from "../../components/Divider.svelte";
    import Gap from "../../components/Gap.svelte";
    import mapstyle from "./mapstyle";
    import Animate from "../../components/Animate.svelte";
    import ButtonRow from "../../components/ButtonRow.svelte";
    import { t } from "../../helpers/translation";

    type MapMarker = {
        name: string;
        lat: number;
        lng: number;
        street: string;
        city: string;
        icon?: string;
        ansprechpartner?: string;
        ansprechpartner_work?: string;
        tel?: string;
        mail?: string;
        gmaps_link: string;
        marker?: google.maps.Marker;
    };

    //-----
    const google_map_api_key = "AIzaSyCPRXZzgFE415FYblP7BYseCeR5nIrvvsU";
    let standorte_nav: HTMLElement;
    let map_wrapper: HTMLElement;
    let google_maps_accepted = false;
    let map: google.maps.Map;
    let active_marker: MapMarker;
    const markers: MapMarker[] = [
        {
            name: $t("page.locations.locations.1.name"),
            lat: Number.parseFloat($t("page.locations.locations.1.lat")),
            lng: Number.parseFloat($t("page.locations.locations.1.lng")),
            street: $t("page.locations.locations.1.street"),
            city: $t("page.locations.locations.1.city"),
            ansprechpartner: $t("page.locations.locations.1.ansprechpartner"),
            ansprechpartner_work: $t(
                "page.locations.locations.1.ansprechpartner_work",
            ),
            tel: $t("page.locations.locations.1.tel"),
            mail: $t("page.locations.locations.1.mail"),
            gmaps_link: $t("page.locations.locations.1.gmaps_link"),
        },
        {
            name: $t("page.locations.locations.2.name"),
            lat: Number.parseFloat($t("page.locations.locations.2.lat")),
            lng: Number.parseFloat($t("page.locations.locations.2.lng")),
            street: $t("page.locations.locations.2.street"),
            city: $t("page.locations.locations.2.city"),
            ansprechpartner: $t("page.locations.locations.2.ansprechpartner"),
            ansprechpartner_work: $t(
                "page.locations.locations.2.ansprechpartner_work",
            ),
            tel: $t("page.locations.locations.2.tel"),
            mail: $t("page.locations.locations.2.mail"),
            gmaps_link: $t("page.locations.locations.2.gmaps_link"),
        },
        {
            name: $t("page.locations.locations.3.name"),
            lat: Number.parseFloat($t("page.locations.locations.3.lat")),
            lng: Number.parseFloat($t("page.locations.locations.3.lng")),
            street: $t("page.locations.locations.3.street"),
            city: $t("page.locations.locations.3.city"),
            ansprechpartner: $t("page.locations.locations.3.ansprechpartner"),
            ansprechpartner_work: $t(
                "page.locations.locations.3.ansprechpartner_work",
            ),
            tel: $t("page.locations.locations.3.tel"),
            mail: $t("page.locations.locations.3.mail"),
            gmaps_link: $t("page.locations.locations.3.gmaps_link"),
        },
        {
            name: $t("page.locations.locations.4.name"),
            lat: Number.parseFloat($t("page.locations.locations.4.lat")),
            lng: Number.parseFloat($t("page.locations.locations.4.lng")),
            street: $t("page.locations.locations.4.street"),
            city: $t("page.locations.locations.4.city"),
            ansprechpartner: $t("page.locations.locations.4.ansprechpartner"),
            ansprechpartner_work: $t(
                "page.locations.locations.4.ansprechpartner_work",
            ),
            tel: $t("page.locations.locations.4.tel"),
            mail: $t("page.locations.locations.4.mail"),
            gmaps_link: $t("page.locations.locations.4.gmaps_link"),
        },
        {
            name: $t("page.locations.locations.5.name"),
            lat: Number.parseFloat($t("page.locations.locations.5.lat")),
            lng: Number.parseFloat($t("page.locations.locations.5.lng")),
            street: $t("page.locations.locations.5.street"),
            city: $t("page.locations.locations.5.city"),
            ansprechpartner: $t("page.locations.locations.5.ansprechpartner"),
            ansprechpartner_work: $t(
                "page.locations.locations.5.ansprechpartner_work",
            ),
            tel: $t("page.locations.locations.5.tel"),
            mail: $t("page.locations.locations.5.mail"),
            gmaps_link: $t("page.locations.locations.5.gmaps_link"),
        },
    ];

    const accept_maps_cookie = async () => {
        google_maps_accepted = true;
        Cookies.set("google-maps-accepted", "true");
        // console.log("accept maps cookie");
        await init_map();
    };

    onMount(async () => {
        const maps_cookie = Boolean(Cookies.get("google-maps-accepted"));

        if (maps_cookie == false) {
            // console.log("maps not accepted yet");
            return;
        }

        google_maps_accepted = maps_cookie;
        // console.log("init map on mount");
        await init_map();
    });

    const init_map = async () => {
        const options: LoaderOptions = {
            /* todo */
        };
        const loader = new Loader(google_map_api_key, options);
        const google = await loader.load();
        const germany = { lat: 51.2324497, lng: 10.0988721 };
        map = new google.maps.Map(document.getElementById("map"), {
            center: germany,
            zoom: 11,
            styles: mapstyle as google.maps.MapTypeStyle[],
        });

        markers.forEach((marker) => {
            const map_marker = new google.maps.Marker({
                position: new google.maps.LatLng(marker.lat, marker.lng),
                title: marker.name,
                animation: google.maps.Animation.DROP,
            });
            marker.marker = map_marker;
            marker.marker.setMap(map);
        });

        const focus_first_marker_on_load = () =>
            focus_marker(markers[0], false);

        setTimeout(focus_first_marker_on_load, 1000);
    };

    const focus_marker = (marker: MapMarker, focus_map = true) => {
        map.panTo(marker.marker.getPosition());
        marker.marker.setAnimation(google.maps.Animation.BOUNCE);
        setTimeout(() => marker.marker.setAnimation(null), 1800);
        active_marker = marker;
        focus_map &&
            window.scrollTo({
                top:
                    map_wrapper.getBoundingClientRect().top -
                    document.body.getBoundingClientRect().top -
                    120,
                behavior: "smooth",
            });
    };
</script>

<LightTheme />

<Hero headline={$t("page.locations.hero.headline")} small gray />

<Container max="800px">
    <Headline>
        {@html $t("page.locations.headline")}
    </Headline>
    <Subline>
        {@html $t("page.locations.subline")}
    </Subline>
    <div id="scroll-map" />
</Container>

<div class="map-container">
    <div class="standorte-nav" bind:this={standorte_nav}>
        <IconImg svg={MapIcon} color="var(--yellow)" size="4em" />
        <Gap height=".5em" />
        <Subline color="var(--yellow)">
            {@html $t("page.locations.headline")}
        </Subline>
        <Divider color="var(--yellow)" />
        <Animate children=".standort-link" delay={1} delay_each={0.2}>
            <div class="inner">
                {#each markers as marker}
                    <div
                        class="standort-link"
                        class:active={active_marker == marker}
                    >
                        <span
                            class="marker-name"
                            on:click={() => focus_marker(marker)}
                        >
                            {@html marker.name}
                        </span>
                        <br />
                        <span class="marker-description">
                            {marker.street} · {marker.city} <br />
                            {marker.ansprechpartner} · {marker.ansprechpartner_work}
                            <br />
                            <a href={"tel:" + marker.tel}>{marker.tel}</a> ·
                            <a href={"mailto:" + marker.mail}>{marker.mail}</a>
                            <br />
                            <Btn
                                small
                                arrow
                                href={`${marker.gmaps_link}`}
                                newtab
                            >
                                {@html $t("page.locations.btn.showonmap")}
                            </Btn>
                        </span>
                    </div>
                {/each}
            </div>
        </Animate>
    </div>

    <div
        id="map"
        bind:this={map_wrapper}
        style="background-color: var(--grayDark)"
    >
        {#if google_maps_accepted === false}
            <div id="accept-maps">
                <p>
                    {@html $t("page.locations.datapolicy.p")}
                </p>
                <br />
                <Btn blinking on:click={accept_maps_cookie}>
                    {@html $t("page.locations.datapolicy.btn")}
                </Btn>
            </div>
        {/if}
    </div>
</div>

<Container max="800px">
    <Gap height="6rem" />
    <Subline>
        {@html $t("page.locations.joinus.headline")}
    </Subline>
    <p>
        {@html $t("page.locations.joinus.p")}
    </p>
    <ButtonRow>
        <Btn href="karriere" arrow>
            {@html $t("page.locations.joinus.btn.tojobs")}
        </Btn>
        <Btn href="kontakt" arrow yellow>
            {@html $t("page.locations.joinus.btn.contact")}
        </Btn>
    </ButtonRow>
</Container>

<style lang="scss">
    @import "../../vars";

    .map-container {
        position: relative;

        #map {
            position: relative;
            width: 100%;
            height: auto;
            min-height: 850px; // adjust for left positon panel
            background-color: orange;
            @media (max-width: 800px) {
                min-height: 600px;
            }
        }

        #accept-maps {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            z-index: 10;
            text-align: center;
            padding: 2rem;
            border-radius: 1rem;
            background-color: rgba($grayDark2, 0.8);
            backdrop-filter: blur(5px);
            p {
                width: 100%;
                max-width: 500px;
            }
        }

        .standorte-nav {
            position: absolute;
            background: rgba($grayDark, 0.8);
            left: 2em;
            top: 50%;
            transform: translateY(-50%);
            width: auto;
            min-width: 500px;
            max-height: 85%;
            overflow-y: scroll;
            padding: 2em;
            z-index: 1;

            @media (max-width: 1000px) {
                position: relative;
                height: auto;
                width: 100%;
                top: unset;
                left: unset;
                transform: unset;
            }
            .inner {
                display: flex;
                gap: 1em;
                flex-direction: column;
                padding-right: 4em;
                padding-bottom: 2em;

                .standort-link {
                    position: relative;
                    color: white;
                    transition: color 0.5s;
                    &.active {
                        .marker-name {
                            color: $green;
                            color: $yellow;
                            font-weight: 700;
                            &::before {
                                display: block;
                            }
                        }
                    }
                    .marker-name {
                        cursor: pointer;
                        font-size: 1.5em;
                        transition: 0.2s;
                        &:hover {
                            color: $green;
                        }
                        &::before {
                            display: none;
                            content: "";
                            position: absolute;
                            width: 0.25em;
                            height: 0.25em;
                            left: -0.5em;
                            top: 0.33em;
                            border-radius: 100%;
                            font-weight: 900;
                            font-size: 1.4em;
                            animation: blinking 1s infinite forwards;
                        }
                    }
                    .marker-description {
                        font-size: 0.8em;
                        :global(.btn) {
                            font-size: 0.75em !important;
                            padding: 0.4em 0.8em;
                        }
                    }
                }
            }
        }
    }
</style>
